import {
    FloatingPortal,
    autoUpdate,
    flip,
    useClick,
    useDismiss,
    useFloating,
    useInteractions,
    useRole,
} from '@floating-ui/react'
import { Fragment, useMemo } from 'react'
import { Flex, SVGs } from '../..'
import Input from '../Input'
import Label from '../Label'
import SVG from '../SVG'
import { useSelect } from './hooks'
import type { Option, SelectProps } from './props'
import {
    StyledIndicator,
    StyledIndicators,
    StyledMenu,
    StyledOption,
    StyledOptions,
    StyledSelect,
    StyledValue,
} from './styles'

/**
 * Select component for forms and other uses.
 * @example
 * const options = [
 *  { label: 'Option 1', value: 'option1' },
 *  { label: 'Option 2', value: 'option2' },
 *  { label: 'Option 3', value: 'option3' },
 * ]
 * const [value, setValue] = useState(options[0])
 *
 * return (
 *   <Select
 *     label="Select"
 *     options={options}
 *     value={value}
 *     onChange={setValue}
 *   />
 * )
 */
const Select = <O extends Option>({
    customLabel,
    isClearable = false,
    isSearchable = false,
    label,
    menuMaxHeight = 205,
    noOptionsMessage = 'No options',
    onChange = () => undefined,
    options = [],
    showUnselectOption = false,
    unselectText = 'None',
    value,
    ...props
}: SelectProps<O>) => {
    const {
        filteredOptions,
        isMenuOpen,
        menuWidth,
        onChangeInput,
        onChangeOption,
        onMouseDownOption,
        onClickInput,
        searchWord,
        selectRef,
        selectText,
        selectedOption,
        showClearIcon,
        showClearIconDelay,
    } = useSelect({
        isClearable,
        isSearchable,
        onChange,
        options,
        value,
    })

    const { refs, floatingStyles, context } = useFloating({
        middleware: [flip()],
        open: isMenuOpen,
        onOpenChange: onClickInput,
        placement: 'bottom',
        whileElementsMounted: autoUpdate,
    })

    const click = useClick(context)
    const dismiss = useDismiss(context)
    const role = useRole(context, { role: 'menu' })

    // Merge all the interactions into prop getters
    const { getReferenceProps, getFloatingProps } = useInteractions([
        click,
        dismiss,
        role,
    ])

    const optionsList = useMemo(
        () =>
            filteredOptions.length > 0 ? (
                filteredOptions.map((option, key) => (
                    <Fragment key={key}>
                        {key === 0 && showUnselectOption && (
                            <StyledOption
                                onMouseDown={() => onMouseDownOption(undefined)}
                                unselect
                            >
                                {unselectText}
                            </StyledOption>
                        )}
                        <StyledOption
                            isSelectedOption={
                                JSON.stringify(option) ===
                                JSON.stringify(selectedOption)
                            }
                            onMouseDown={() => onMouseDownOption(option)}
                        >
                            {customLabel?.(option) ?? option.label}
                        </StyledOption>
                    </Fragment>
                ))
            ) : (
                <StyledOption>{noOptionsMessage}</StyledOption>
            ),
        [
            filteredOptions,
            noOptionsMessage,
            showUnselectOption,
            unselectText,
            selectedOption,
            customLabel,
            onMouseDownOption,
        ]
    )

    const inputDiv = useMemo(
        () => (
            <Input
                {...props}
                css={{ cursor: isSearchable ? 'text' : 'pointer' }}
                onChange={onChangeInput}
                readOnly={!isSearchable || props.readOnly}
                value={searchWord}
            />
        ),
        [isSearchable, onChangeInput, props, searchWord]
    )

    const valueDiv = selectedOption && (
        <StyledValue>
            {customLabel?.(selectedOption) ?? selectedOption.label}
        </StyledValue>
    )

    const labelDiv = useMemo(
        () =>
            !!label && (
                <Label>
                    {label}
                    {props.required && '*'}
                </Label>
            ),
        [label, props.required]
    )

    const indicators = useMemo(
        () => (
            <StyledIndicators disabled={props.disabled}>
                <StyledIndicator>
                    <SVG src={SVGs.ChevronDown} />
                </StyledIndicator>
                {showClearIconDelay && (
                    <StyledIndicator
                        autoPointerEvents={!props.disabled}
                        showIndicator={showClearIcon}
                    >
                        <SVG
                            onClick={() => onChangeOption(undefined)}
                            src={SVGs.Close}
                        />
                    </StyledIndicator>
                )}
            </StyledIndicators>
        ),
        [onChangeOption, props.disabled, showClearIcon, showClearIconDelay]
    )

    const menu = useMemo(
        () =>
            isMenuOpen && (
                <FloatingPortal>
                    <StyledMenu
                        css={{ w: menuWidth }}
                        ref={refs.setFloating}
                        style={floatingStyles}
                        {...getFloatingProps()}
                    >
                        <StyledOptions style={{ maxHeight: menuMaxHeight }}>
                            {optionsList}
                        </StyledOptions>
                    </StyledMenu>
                </FloatingPortal>
            ),
        [
            floatingStyles,
            getFloatingProps,
            isMenuOpen,
            menuMaxHeight,
            menuWidth,
            optionsList,
            refs.setFloating,
        ]
    )

    return (
        <StyledSelect
            hasLabel={!!label}
            ref={selectRef}
            valueSelected={selectText !== ''}
        >
            <Flex
                alignItems="center"
                css={{
                    display: 'inline-flex',
                    position: 'relative',
                    w: '100%',
                }}
                ref={refs.setReference}
                {...getReferenceProps()}
            >
                {inputDiv}
                {valueDiv}
                {labelDiv}
            </Flex>
            {indicators}
            {menu}
        </StyledSelect>
    )
}

export default Select

export { StyledSelect }
